import { useEffect, useState, useSyncExternalStore } from "react";
import { CLUSTER_HUES } from "./traceAnatomy.js";
import { browserStorageOwnerIsCurrent, currentBrowserStorageOwner } from "./browserStorageScope.js";
import {
  dropKeptRegisterReading,
  keepRegisterReading,
  readKeptRegisterReading,
  subscribeKeptRegisterReadingCleared,
} from "./registerReadingStore.js";

// The work register as a static reading: the organization's groups, the areas of
// work each group owns, the people and tools behind them, and the norms and
// intents a register build read out of their sessions.
//
// Until builds are served, a build's lens data reaches the page one of two ways.
// On a dev server it can be mounted as a static asset at REGISTER_SNAPSHOT_URL:
// `dashboard/public/register-snapshot.local.json`. The repository ignores every
// `*.local.*` file, so the tenant content a snapshot carries never enters git.
// It sits outside `public/assets/`, which the Pyodide asset fetch owns and
// rejects any other entry in. Anywhere else, including a deployed build, the
// viewer opens the file in the tab (openRegisterFile), and for a signed-in
// viewer the browser keeps it so a reload restores it (registerReadingStore).
export const REGISTER_SNAPSHOT_URL = "/register-snapshot.local.json";

// Groups take the house topic hues minus green: green marks the selection in
// every map, so a group coloured green would read as selected.
export const GROUP_HUES = CLUSTER_HUES.filter((h) => h !== CLUSTER_HUES[0]);

const NORM_LEVELS = { universal: 4, common: 3, occasional: 2, rare: 1 };
const INTENT_LEVELS = { major: 2, minor: 1 };

/** Dots (of 4) for a norm's prevalence, or (of 2) for an intent's weight. */
export function levelDots(level, kind = "norm") {
  if (kind === "intent") return { filled: INTENT_LEVELS[level] ?? 0, of: 2 };
  return { filled: NORM_LEVELS[level] ?? 0, of: 4 };
}

// A scoped readings file is what a served reading endpoint would answer, precomputed: one reading per Explore
// scope key (`org`, `models:<id>`, `mcp_servers:<id>`, ...) over the register tree. dashboard/work-register/readings
// builds one from a finished register run.
function isReadings(data) {
  return Boolean(data && data.kind === "register-readings" && data.readings && data.readings.org && Array.isArray(data.tree));
}

function isRegister(data) {
  return isReadings(data) || Boolean(data && data.nodes && data.nodes.org && Array.isArray(data.groups));
}

// A normalized file keeps each statement once: its reading rows name a practice and the item that words it there, or
// a concern, and the file's `items`, `practices` and `concerns` tables hold the text. Joining them on load gives a
// reading (and each of its parts) the same rows whichever shape the file has; a row that names nothing stays as is.
function joinReadings(data) {
  const items = data.items || {};
  const practices = data.practices || {};
  const concerns = data.concerns || {};
  const nodes = new Map(data.tree.map((n) => [n.id, n]));
  const practiceRow = (r) => {
    const it = r.item ? items[r.item] : null;
    if (!it) return r;
    const pr = practices[r.practice] || {};
    return {
      kind: it.kind,
      statement: it.statement,
      deviation: it.deviation ?? null,
      prevalence: it.prevalence ?? null,
      node: it.node,
      node_name: nodes.get(it.node)?.name ?? null,
      stated_at: pr.stated_at || [it.node],
      org_statement: pr.org_item ? items[pr.org_item]?.statement ?? null : null,
      ...r,
    };
  };
  const concernRow = (r) => {
    const c = r.concern ? concerns[r.concern] : null;
    return c ? { id: r.concern, statement: c.statement, deviation: c.deviation ?? null, node: c.node, act: c.act ?? null, ...r } : r;
  };
  const lists = (p) => p && {
    ...p,
    observed: (p.observed || []).map(practiceRow),
    carried: (p.carried || []).map(practiceRow),
    organization: (p.organization || []).map(practiceRow),
  };
  const one = (x) => ({ ...x, practices: lists(x.practices), concerns: (x.concerns || []).map(concernRow) });
  const out = {};
  for (const [key, r] of Object.entries(data.readings)) {
    const parts = {};
    for (const [id, p] of Object.entries(r.parts || {})) parts[id] = one(p);
    out[key] = { ...one(r), parts };
  }
  return out;
}

/** A scoped readings file for the lens: its readings, index and the tree the house map draws. */
export function indexReadings(data) {
  const byId = new Map();
  const childrenOf = new Map();
  for (const n of data.tree) {
    const name = n.kind === "org" ? "Organization" : n.kind === "region" && n.name && n.name !== n.id ? `${n.id}: ${n.name}` : n.name || n.id;
    byId.set(n.id, { id: n.id, name, parentId: n.parent || null });
    childrenOf.set(n.id, []);
  }
  for (const n of data.tree) {
    if (n.parent && childrenOf.has(n.parent)) childrenOf.get(n.parent).push(n.id);
  }
  return {
    kind: "readings",
    build: data.build || {},
    index: data.index || [],
    readings: joinReadings(data),
    nodes: new Map(data.tree.map((n) => [n.id, n])),
    tree: { byId, childrenOf, roots: ["org"] },
  };
}

// A reading's map is sized by the organization's work, which leaves a group that holds little of it, or a band where
// a small scope's work sits, too thin to find. Such a band keeps this share of its parent's span, room for its label.
const BAND_FLOOR = 0.06;
const LIT_FLOOR = 0.05;

/**
 * The map's `shareOf` for a reading: each band's share of its parent in the organization's work, with every group
 * and every band holding at least LIT_FLOOR of this reading's work kept to BAND_FLOOR of its parent's span.
 */
export function readingShareOf(R, orgMass, readingMass) {
  return (id) => {
    if (id === "org") return 1;
    const n = R.nodes.get(id);
    const p = n?.parent;
    const share = p && orgMass[p] ? (orgMass[id] || 0) / orgMass[p] : 0;
    const keep = n?.kind === "region" || (readingMass[id] || 0) >= LIT_FLOOR;
    return keep ? Math.max(share, BAND_FLOOR) : share;
  };
}

/** The reading key for an Explore scope path: its last step's dimension and member, or the organization. */
export function readingKeyFor(scopePath) {
  const last = scopePath && scopePath.length ? scopePath[scopePath.length - 1] : null;
  return last ? `${last.dim}:${last.id}` : "org";
}

// The walks below track the nodes they visit: a reading can come from a file
// the viewer opened, and a malformed parent or child link must not hang the
// tab or overflow the stack.

/** The node's group id (a region), or null for the organization. */
export function groupOf(nodes, id) {
  const seen = new Set();
  let cur = nodes[id];
  while (cur && cur.kind !== "region" && !seen.has(cur.id)) {
    seen.add(cur.id);
    cur = cur.parent ? nodes[cur.parent] : null;
  }
  return cur?.kind === "region" ? cur.id : null;
}

/** Ancestors from the organization down to (and including) the node. */
export function pathOf(nodes, id) {
  const out = [];
  let cur = nodes[id];
  const seen = new Set();
  while (cur && !seen.has(cur.id)) {
    out.unshift(cur);
    seen.add(cur.id);
    cur = cur.parent ? nodes[cur.parent] : null;
  }
  return out;
}

/** Every planned read under a node, its own and its descendants'. */
export function subtreeReads(nodes, id) {
  const out = [];
  const seen = new Set();
  const walk = (nid) => {
    const node = nodes[nid];
    if (!node || seen.has(nid)) return;
    seen.add(nid);
    out.push(...(node.reads || []));
    for (const c of node.children || []) walk(c);
  };
  walk(id);
  return out;
}

/** A group's display name: its id plus what it works on. */
export function groupName(group) {
  return group?.descriptor ? `${group.id}: ${group.descriptor}` : group?.id ?? "";
}

/**
 * Sessions per day for a node, per group, aligned to the reading's days. The
 * organization's series is split by group; below it a node carries one total,
 * which belongs to the node's own group.
 * Returns [{ day, total, parts: [{ group, n }] }].
 */
export function dailyByGroup(node, days, groupIds, ownGroup = null) {
  const by = node?.daily?.by_group;
  const total = node?.daily?.total;
  return (days || []).map((day, i) => {
    const parts = by
      ? groupIds.map((g) => ({ group: g, n: Number(by[g]?.[i]) || 0 })).filter((p) => p.n > 0)
      : Number(total?.[i]) > 0 ? [{ group: ownGroup, n: Number(total[i]) }] : [];
    return { day, total: parts.reduce((a, p) => a + p.n, 0), parts };
  });
}

/**
 * How many of a node's own people had a session read, and how many readers
 * came from elsewhere (an area's reads can land on another group's people).
 */
export function readCoverage(nodes, id) {
  const members = new Set((nodes[id]?.people || []).map((p) => p.label));
  const readers = new Set(subtreeReads(nodes, id).map((r) => r.actor));
  let inside = 0;
  for (const a of readers) if (members.has(a)) inside += 1;
  return { members: members.size, read: inside, outside: readers.size - inside };
}

/**
 * Index a reading for the lens: the tree the house map draws, sessions as the
 * map's heat, and the map's sizes. Groups split the organization by people (one
 * automation-heavy person can run most of a tenant's sessions, which would
 * shrink everyone else to a sliver); areas split their parent by sessions, and
 * a group's work that no area holds stays as open space.
 */
export function indexRegister(data) {
  const nodes = data.nodes;
  const groups = data.groups;
  const hue = new Map(groups.map((g, i) => [g.id, GROUP_HUES[i % GROUP_HUES.length]]));
  const groupById = new Map(groups.map((g) => [g.id, g]));
  const byId = new Map();
  const childrenOf = new Map();
  for (const n of Object.values(nodes)) {
    const name = n.kind === "region" ? groupName(groupById.get(n.id) || { id: n.id }) : n.id === "org" ? data.meta?.org_label || "Organization" : n.label;
    byId.set(n.id, { id: n.id, name, parentId: n.parent || null });
    childrenOf.set(n.id, [...(n.children || [])].filter((c) => nodes[c]));
  }
  const activity = new Map(Object.values(nodes).map((n) => [n.id, n.sessions || 0]));
  const totalPeople = nodes.org.people?.length || groups.reduce((a, g) => a + (g.people || 0), 0) || 1;
  const shareOf = (id) => {
    const n = nodes[id];
    if (!n || n.id === "org") return 1;
    if (n.kind === "region") return (groupById.get(id)?.people || 0) / totalPeople;
    const parent = nodes[n.parent];
    return parent && parent.sessions ? (n.sessions || 0) / parent.sessions : 0;
  };
  const hueOf = (id) => {
    const g = groupOf(nodes, id);
    return g ? hue.get(g) ?? null : null;
  };
  return {
    meta: data.meta || {},
    groups,
    groupIds: groups.map((g) => g.id),
    hue,
    shared: new Set(data.shared || []),
    nodes,
    tensions: data.tensions || [],
    days: data.days || [],
    tree: { byId, childrenOf, roots: ["org"] },
    activity,
    shareOf,
    hueOf,
  };
}

let pending = null;

/**
 * Fetch the mounted reading once per page. A missing asset reads as "missing"
 * rather than an error: the dev server answers unknown paths with the app
 * shell, so anything that is not a register's JSON counts as absent.
 */
export function loadRegisterSnapshot(fetchImpl = globalThis.fetch) {
  if (!pending) {
    pending = Promise.resolve()
      .then(() => fetchImpl(REGISTER_SNAPSHOT_URL, { credentials: "same-origin" }))
      .then(async (r) => {
        if (!r.ok) return r.status === 404 ? { status: "missing" } : { status: "error", error: `HTTP ${r.status}` };
        if (!String(r.headers.get("content-type") || "").includes("json")) return { status: "missing" };
        const data = await r.json();
        if (!isRegister(data)) return { status: "missing" };
        return { status: "ready", data: isReadings(data) ? indexReadings(data) : indexRegister(data) };
      })
      .catch((e) => ({ status: "error", error: e?.message || String(e) }));
  }
  return pending;
}

// A reading opened from a file is parsed in the tab and never uploaded. It
// carries the storage owner it was opened under, and readers show it only to
// that owner, so an identity change never shows another account's reading.
let opened = null;
const openedListeners = new Set();
const subscribeOpened = (fn) => {
  openedListeners.add(fn);
  return () => openedListeners.delete(fn);
};
const readOpened = () => opened;
function setOpened(next) {
  opened = next;
  openedListeners.forEach((fn) => fn());
}
subscribeKeptRegisterReadingCleared((ownerKey) => {
  if (opened?.ownerKey === ownerKey) setOpened(null);
});

function indexFileText(text, name) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    return { status: "invalid", error: `${name} is not a JSON file.` };
  }
  if (!isRegister(data)) return { status: "invalid", error: `${name} is not a register reading.` };
  try {
    return { status: "ready", data: isReadings(data) ? indexReadings(data) : indexRegister(data), source: name };
  } catch (e) {
    return { status: "invalid", error: `${name} could not be read (${e?.message || e}).` };
  }
}

/**
 * Read a register build's lens data from a file the viewer picked. Resolves
 * { status: "ready", data, source, kept } (and every mounted reader follows
 * it), or { status: "invalid", error } leaving any open reading in place.
 * `kept` says whether the browser holds it for the next load.
 */
export async function openRegisterFile(file) {
  let text;
  try {
    text = await file.text();
  } catch {
    return { status: "invalid", error: `${file.name} is not a JSON file.` };
  }
  const read = indexFileText(text, file.name);
  if (read.status !== "ready") return read;
  const owner = currentBrowserStorageOwner();
  const kept = await keepRegisterReading(owner, { name: file.name, text });
  setOpened({ ...read, kept, ownerKey: owner?.key ?? null });
  return opened;
}

// One restore per owner per page load, so a remount does not read the store
// again and a reading forgotten since is not brought back.
let restore = { key: undefined, settled: false, promise: null };

/**
 * Restore the reading this browser keeps for the viewer. Resolves the reading
 * now open for them: the restored copy, a file they opened while the restore
 * ran, or null.
 */
export function restoreKeptReading() {
  const owner = currentBrowserStorageOwner();
  const key = owner?.key ?? null;
  if (restore.promise && restore.key === key) return restore.promise;
  const entry = { key, settled: false, promise: null };
  entry.promise = (async () => {
    const kept = await readKeptRegisterReading(owner);
    const read = kept ? indexFileText(kept.text, kept.name || "the kept reading") : null;
    const reading = read?.status === "ready" ? { ...read, kept: true, ownerKey: key } : null;
    // A damaged record reads as nothing kept, and is dropped.
    if (kept && !reading) await dropKeptRegisterReading(owner);
    entry.settled = true;
    // A file opened while this ran is newer than the kept copy, and a restore
    // that outlived an identity change belongs to no one on screen.
    if (reading && browserStorageOwnerIsCurrent(owner) && opened?.ownerKey !== key) setOpened(reading);
    return opened?.ownerKey === key ? opened : null;
  })();
  restore = entry;
  return entry.promise;
}

/** Close the opened reading and delete the copy this browser keeps. */
export async function forgetRegisterReading() {
  const owner = currentBrowserStorageOwner();
  setOpened(null);
  await dropKeptRegisterReading(owner);
}

/** Test seam: a reload. Forgets the cached load and the in-memory reading; the kept copy stays. */
export function resetRegisterSnapshot() {
  pending = null;
  opened = null;
  restore = { key: undefined, settled: false, promise: null };
}

// Only a dev server can hold the mount: a deployed build comes from a clean
// checkout, which never has a `*.local.*` file, so it does not ask. Read
// directly so a production build folds it to false.
const mountServed = () => Boolean(import.meta.env.DEV);

export function useRegisterSnapshot() {
  const ownerKey = currentBrowserStorageOwner()?.key ?? null;
  const [mount, setMount] = useState(() => (mountServed() ? { status: "loading" } : { status: "missing" }));
  // Only a signed-in viewer can have a kept copy to wait for.
  const [restoring, setRestoring] = useState(() => ownerKey !== null && !(restore.key === ownerKey && restore.settled));
  const file = useSyncExternalStore(subscribeOpened, readOpened, readOpened);
  useEffect(() => {
    let on = true;
    restoreKeptReading().then(() => { if (on) setRestoring(false); });
    if (mountServed()) loadRegisterSnapshot().then((s) => { if (on) setMount(s); });
    return () => { on = false; };
  }, []);
  if (file && file.ownerKey === ownerKey) return file;
  return restoring ? { status: "loading" } : mount;
}
